/**
 * Stimulus mixin for managing submit button state across multiple operations.
 * Uses a lock-based system where multiple operations can disable the button,
 * but it only gets re-enabled when all locks are released.
 * 
 * Usage:
 * import { useSubmitButtonManager } from "../mixins/useSubmitButtonManager";
 * 
 * export default class extends Controller {
 *   connect() {
 *     useSubmitButtonManager(this);
 *   }
 * 
 *   someOperation() {
 *     this.disableSubmitButton('some-unique-id', 'Processing...');
 *     // ... do work ...
 *     this.enableSubmitButton('some-unique-id');
 *   }
 * }
 */
export const useSubmitButtonManager = (controller) => {
  Object.assign(controller, {
    getSubmitButton() {
      const form = this.element.closest('form');
      return form ? form.querySelector('input[type=submit]') : null;
    },

    _getSubmitButtonState(submitButton) {
      // Store state on the button element itself, so multiple connected controllers can have shared access
      if (!submitButton._lockState) {
        submitButton._lockState = {
          locks: new Set(),
          originalValue: null
        };
      }
      return submitButton._lockState;
    },

    disableSubmitButton(lockId, message = 'Working...') {
      const submitButton = this.getSubmitButton();
      if (!submitButton) return;

      const state = this._getSubmitButtonState(submitButton);
      
      // Store original button text only on first lock
      if (state.locks.size === 0) {
        state.originalValue = submitButton.value;
      }
      
      state.locks.add(lockId);
      submitButton.disabled = true;
      submitButton.value = message;
    },

    enableSubmitButton(lockId) {
      const submitButton = this.getSubmitButton();
      if (!submitButton) return;

      const state = this._getSubmitButtonState(submitButton);
      if (!state.locks.has(lockId)) return;

      state.locks.delete(lockId);
      
      if (state.locks.size === 0) {
        submitButton.disabled = false;
        submitButton.value = state.originalValue;
        state.originalValue = null;
      }
    }
  });
};
